import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { Channel } from 'stream-chat';

interface ChannelContextType {
  selectedChannel: Channel | null;
  setSelectedChannel: (channel: Channel | null) => void;
}

/**
 * CONTEXT CREATION - Create channel context with undefined default
 * Default is undefined (not null) so we can detect if Provider is missing.
 * If someone calls useChannelContext() outside Provider, we throw error.
 */
const ChannelContext = createContext<ChannelContextType | undefined>(undefined);

/**
 * CHANNEL PROVIDER - Provides selected channel state to all children
 * Wraps the app (in BootComponent) to make selectedChannel accessible everywhere.
 * STATE MANAGEMENT:
 * - selectedChannel: The currently active channel (null when no channel is open)
 * - setSelectedChannel: Updates the active channel
 * TYPICAL FLOW:
 * 1. User clicks channel in sidebar
 * 2. onClick handler calls setSelectedChannel(channel)
 * 3. All components using useChannelContext() see the update
 * 4. ChannelInner renders the selected channel
 * 5. User clicks back button
 * 6. Back handler calls setSelectedChannel(null)
 * 7. App returns to channel list view
 */
export const ChannelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  /** Tracks which channel is currently selected/active, null when viewing channel list */
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);

  return (
    <ChannelContext.Provider value={{ selectedChannel, setSelectedChannel }}>
      {children}
    </ChannelContext.Provider>
  );
};

/**
 * CUSTOM HOOK - Easy access to channel context
 * Simplifies consuming ChannelContext with built-in error checking.
 * USAGE:
 * const { selectedChannel, setSelectedChannel } = useChannelContext();
 * ERROR HANDLING:
 * Throws error if called outside ChannelProvider (helps catch mistakes early)
 */
export const useChannelContext = () => {
  const context = useContext(ChannelContext);
  if (!context) {
    throw new Error('useChannelContext must be used within a ChannelProvider');
  }
  return context;
};
